<template>
  <div class="card kpi-card">
    <div class="card-body">
      <div class="kpi-header">
        <div>
          <h6>{{ $t("dashboard.stylesCreatedForCustomers") }}</h6>
          <small class="text-muted">
            {{ $t("dashboard.stylesCreatedSubtitle") }}
          </small>
        </div>
      </div>

      <div v-if="loading" class="chart-wrapper" style="align-items: center; justify-content: center; min-height: 180px">
        <GridLoading :infoText="$t('dashboard.loading')" />
      </div>

      <div v-else-if="topFiveData.length" class="chart-wrapper">
        <div class="chart-main">
          <div class="plot-area">
            <div
              v-for="tick in yTicks"
              :key="tick"
              class="grid-row"
              :style="{ bottom: (tick / maxYAxisValue) * 100 + '%' }"
            >
              <span class="tick-label">{{ tick }}</span>
              <span class="grid-line"></span>
            </div>

            <div class="bars-row">
              <div v-for="(item, idx) in topFiveData" :key="item.name + '-' + idx" class="bar-col">
                <div
                  class="bar-rect"
                  :style="{
                    height: getBarHeight(item.count),
                    background: barColor(idx)
                  }"
                >
                  <div class="bar-tooltip">
                    <strong>{{ item.name }}</strong>
                    <div class="tooltip-row">
                      <span class="tooltip-box"></span>
                      <span>{{ $t("dashboard.stylesCount", { count: item.count }) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="x-axis">
            <div v-for="(item, idx) in topFiveData" :key="item.name + '-' + idx" class="x-label" :title="item.name">
              {{ item.name }}
            </div>
          </div>
        </div>
      </div>

      <footer v-if="showExpand" class="sw-chart-footer">
        <a ref="expandTrigger" href="#" class="sw-show-all-link" @click.prevent="openModal">
          <i class="fa fa-arrows-alt" aria-hidden="true"></i>
          {{ $t("dashboard.showAllCustomers", { count: sortedData.length }) }}
        </a>
      </footer>
    </div>

    <div v-if="isModalOpen" class="modal-overlay" @click.self="closeModal">
      <div
        class="modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="stylesForCustomersModalTitle"
        tabindex="-1"
        ref="modal"
        @keydown="handleModalKeydown"
      >
        <div class="modal-header">
          <div>
            <h5 id="stylesForCustomersModalTitle">{{ $t("dashboard.stylesCreatedForCustomers") }}</h5>
            <small class="modal-subtitle"> {{ $t("dashboard.allCustomers", { count: sortedData.length }) }} </small>
          </div>

          <div class="modal-actions">
            <button class="nav-btn" :disabled="!canGoPrevious" @click="previousCustomers">
              <i class="fas fa-chevron-left"></i>
            </button>

            <button class="nav-btn" :disabled="!canGoNext" @click="nextCustomers">
              <i class="fas fa-chevron-right"></i>
            </button>

            <button class="modal-close-btn" :aria-label="$t('popup.close')" @click="closeModal">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div class="chart-wrapper modal-chart-wrapper">
          <div class="chart-main">
            <div class="plot-area modal-plot-area">
              <div
                v-for="tick in modalYTicks"
                :key="tick"
                class="grid-row"
                :style="{ bottom: (tick / modalMaxYAxisValue) * 100 + '%' }"
              >
                <span class="tick-label">{{ tick }}</span>
                <span class="grid-line"></span>
              </div>

              <div class="bars-row modal-bars-row">
                <div v-for="(item, idx) in modalVisibleData" :key="item.name + '-' + idx" class="bar-col">
                  <div
                    class="bar-rect modal-bar-rect"
                    :style="{
                      height: getModalBarHeight(item.count),
                      background: barColor(idx)
                    }"
                  >
                    <div class="bar-tooltip">
                      <strong>{{ item.name }}</strong>
                      <div class="tooltip-row">
                        <span class="tooltip-box"></span>
                        <span>{{ $t("dashboard.stylesCount", { count: item.count }) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="x-axis modal-x-axis">
              <div
                v-for="(item, idx) in modalVisibleData"
                :key="item.name + '-' + idx"
                class="x-label modal-x-label"
                :title="item.name"
              >
                {{ item.name }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useToast } from "vue-toastification";

import { DashboardApi } from "@/api";
import { i18n } from "@/core/localization";
import GridLoading from "../GridLoading.vue";

export default defineComponent({
  name: "StylesCreatedForCustomers",

  components: {
    GridLoading
  },

  setup() {
    const isModalOpen = ref(false);
    const modal = ref(null);
    const expandTrigger = ref(null);
    const toast = useToast();

    const customers = ref([]);
    const loading = ref(true);

    const fetchCustomers = async () => {
      loading.value = true;

      try {
        const api = new DashboardApi();
        const res = await api.apiDashboardStyleCreatedForCustomersGet();

        const items = res?.data?.items || [];

        customers.value = items.map(x => ({
          name: x.customerName,
          count: x.styleCount
        }));
      } catch (e) {
        console.error("Failed to load styles created for customers", e);
        customers.value = [];
        toast.error(i18n.global.t("dashboard.stylesForCustomersLoadError"), {
          toastClassName: "custom-error-toast",
          timeout: 5000
        });
      } finally {
        loading.value = false;
      }
    };

    const sortedData = computed(() => [...customers.value].filter(x => x.count > 0).sort((a, b) => b.count - a.count));

    const topFiveData = computed(() => sortedData.value.slice(0, 5));

    const showExpand = computed(() => sortedData.value.length > 5);

    const modalVisibleCount = 10;
    const modalStartIndex = ref(0);

    const modalVisibleData = computed(() =>
      sortedData.value.slice(modalStartIndex.value, modalStartIndex.value + modalVisibleCount)
    );

    const canGoPrevious = computed(() => modalStartIndex.value > 0);
    const canGoNext = computed(() => modalStartIndex.value + modalVisibleCount < sortedData.value.length);

    // Adaptive "nice number" axis: pick a round step (1/2/5 x 10^k) targeting
    // ~5 intervals so small datasets get a small axis and tall, readable bars
    // instead of tiny slivers against a fixed 0-20 scale.
    const computeNiceScale = values => {
      const rawMax = Math.max(...values, 0);
      if (rawMax <= 0) return { max: 5, step: 1 };

      const targetIntervals = 5;
      let step = Math.ceil(rawMax / targetIntervals);
      const mag = Math.pow(10, Math.floor(Math.log10(step)));
      const norm = step / mag;
      if (norm <= 1) step = 1 * mag;
      else if (norm <= 2) step = 2 * mag;
      else if (norm <= 5) step = 5 * mag;
      else step = 10 * mag;

      let max = Math.ceil(rawMax / step) * step;
      // keep 5 intervals so the axis reads as an even 5-division scale (like the
      // SGC card) instead of collapsing to a coarse 0-1-2 for small counts
      if (max / step < 5) max = step * 5;
      return { max, step };
    };

    const buildTicks = ({ max, step }) => {
      const ticks = [];
      for (let v = 0; v <= max + 1e-9; v += step) {
        ticks.push(Math.round(v));
      }
      return ticks;
    };

    // Tile axis follows the full dataset (its tallest bar is the global max).
    const niceScale = computed(() => computeNiceScale(customers.value.map(x => x.count)));
    const maxYAxisValue = computed(() => niceScale.value.max);
    const yTicks = computed(() => buildTicks(niceScale.value));

    // Modal axis follows the visible page so later pages (smaller counts) keep
    // tall, readable bars instead of being squashed against the global maximum.
    const modalNiceScale = computed(() => computeNiceScale(modalVisibleData.value.map(x => x.count)));
    const modalMaxYAxisValue = computed(() => modalNiceScale.value.max);
    const modalYTicks = computed(() => buildTicks(modalNiceScale.value));

    const getBarHeight = value => `${(value / maxYAxisValue.value) * 100}%`;
    const getModalBarHeight = value => `${(value / modalMaxYAxisValue.value) * 100}%`;

    const barColor = () => "#2f78b7";

    const openModal = async () => {
      // Always start on the first page when (re)opening the full view.
      modalStartIndex.value = 0;
      isModalOpen.value = true;
      document.body.style.overflow = "hidden";
      await nextTick();
      modal.value?.focus();
    };

    const closeModal = async () => {
      isModalOpen.value = false;
      document.body.style.overflow = "";
      await nextTick();
      // Accessibility: return focus to the control that opened the modal.
      expandTrigger.value?.focus();
    };

    const previousCustomers = () => {
      modalStartIndex.value = Math.max(modalStartIndex.value - modalVisibleCount, 0);
    };

    const nextCustomers = () => {
      if (canGoNext.value) {
        modalStartIndex.value += modalVisibleCount;
      }
    };

    const handleModalKeydown = e => {
      if (e.key === "Escape") {
        closeModal();
        return;
      }

      if (e.key !== "Tab") return;

      // Trap focus within the modal while it is open.
      const el = modal.value;
      if (!el) return;

      const focusable = Array.from(
        el.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      ).filter(node => !node.hasAttribute("disabled"));
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && (active === first || active === el)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    onMounted(() => {
      fetchCustomers();
    });

    onBeforeUnmount(() => {
      document.body.style.overflow = "";
    });

    return {
      isModalOpen,
      modal,
      expandTrigger,
      sortedData,
      topFiveData,
      showExpand,
      yTicks,
      maxYAxisValue,
      getBarHeight,
      modalVisibleData,
      modalYTicks,
      modalMaxYAxisValue,
      getModalBarHeight,
      canGoPrevious,
      canGoNext,
      previousCustomers,
      nextCustomers,
      barColor,
      openModal,
      closeModal,
      handleModalKeydown,
      loading
    };
  }
});
</script>

<style scoped>
.kpi-card {
  font-family: Arial, Helvetica, sans-serif;
  border-radius: 10px;
  height: 100%;
  min-height: 360px;
  background: #ffffff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.card-body {
  padding: 14px 18px;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.kpi-header h6 {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 3px 0;
  color: #222;
}

.text-muted {
  display: block;
  font-size: 13px;
  color: #555 !important;
  line-height: 1.6;
}

.chart-wrapper {
  position: relative;
  display: flex;
  margin-top: 18px;
  padding-top: 18px;
  flex: 1 1 auto;
  min-height: 0;
}

.chart-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.plot-area {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  margin-left: 65px;
  border-bottom: 1px solid #d9d9d9;
  overflow: visible;
}

.grid-row {
  position: absolute;
  left: -58px;
  right: 0;
  display: flex;
  align-items: center;
}

.tick-label {
  width: 48px;
  margin-right: 10px;
  font-size: 13px;
  color: #7b8faa;
  text-align: right;
  white-space: nowrap;
}

.grid-line {
  flex: 1;
  height: 1px;
  background: #c9d9ea;
}

.grid-row:first-child .grid-line {
  display: none;
}

.bars-row {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: space-evenly !important;
  gap: 0 !important;
  padding: 0 !important;
  z-index: 2;
}

.bar-col {
  height: 100%;
  flex: 1;
  max-width: none !important;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  overflow: visible;
}

.bar-rect {
  position: relative;
  min-height: 3px;
  width: 28px !important;
  border-radius: 4px 4px 0 0 !important;
  transition: height 0.4s ease;
  cursor: pointer;
}

.bar-tooltip {
  position: absolute;
  left: 50%;
  bottom: calc(100% + 10px);
  transform: translateX(-50%);
  background: #0054a6;
  color: #ffffff;
  padding: 8px 10px;
  border-radius: 5px;
  width: max-content;
  min-width: 105px;
  max-width: 220px;
  white-space: normal;
  overflow-wrap: break-word;
  text-align: left;
  font-size: 12px;
  font-weight: 400;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  z-index: 50;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.18);
}

.bar-col:first-child .bar-tooltip {
  left: 0;
  right: auto;
  transform: none;
}

.bar-col:last-child .bar-tooltip {
  left: auto;
  right: -40px;
  transform: none;
}

.bar-rect::after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: calc(100% + 3px);
  transform: translateX(-50%);
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-top: 7px solid #0054a6;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  z-index: 51;
}

.bar-rect:hover::after {
  opacity: 1;
  visibility: visible;
}

.bar-tooltip strong {
  display: block;
  font-weight: 700;
  margin-bottom: 5px;
}

.tooltip-row {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.tooltip-box {
  width: 11px;
  height: 11px;
  border: 2px solid #ffffff;
  display: inline-block;
  box-sizing: border-box;
}

.bar-rect:hover .bar-tooltip {
  opacity: 1;
  visibility: visible;
}

.x-axis {
  height: 28px;
  margin-left: 65px;
  display: flex;
  justify-content: space-evenly;
  gap: 0;
  padding: 0;
}

.x-label {
  flex: 1;
  max-width: none;
  font-size: 11px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  margin-top: 6px;
}

/* Footer */
.sw-chart-footer {
  border-top: 1px dashed #d2d8df;
  padding: 5px 18px 6px;
  background: #ffffff;
}

.sw-show-all-link {
  color: #0054a6;
  font-size: 13px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-weight: 600;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.modal-content {
  background: #ffffff;
  width: 92vw;
  max-width: 1600px;
  height: 88vh;
  max-height: 92vh;
  padding: 0;
  border-radius: 12px;
  overflow: visible;
  outline: none;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
}

.modal-header {
  height: 78px;
  padding: 22px 28px 16px;
  border-bottom: 1px solid #e5e9ef;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.modal-header h5 {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: #1f2430;
}

.modal-subtitle {
  font-size: 13px;
  color: #555;
}

/* Header controls — kept identical to the Product-wise SGC card so the two
   bottom KPI cards stay visually consistent. */
.modal-actions {
  display: flex;
  align-items: center;
  gap: 0;
}

.nav-btn {
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  color: #6e87aa;
  border-radius: 4px;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  margin-left: 10px;
}

.nav-btn + .nav-btn {
  margin-left: 2px;
}

.nav-btn:hover {
  background: #f3f6fa;
}

.nav-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.nav-btn:disabled:hover {
  background: transparent;
}

.modal-close-btn {
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  color: #6e87aa;
  border-radius: 4px;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  margin-left: 8px;
}

.modal-close-btn:hover {
  background: #f3f6fa;
}

.modal-chart-wrapper {
  flex: 1 1 auto;
  min-height: 0;
  height: auto !important;
  margin-top: 0;
  padding: 28px 34px 22px 34px;
  box-sizing: border-box;
}

/* Let the plot area grow to fill the taller modal so the chart uses the full
   area instead of sitting in a fixed-height band. */
.modal-chart-wrapper .chart-main {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.modal-plot-area {
  flex: 1 1 auto;
  min-height: 0;
  height: auto !important;
  margin-left: 65px;
}

.modal-bars-row {
  justify-content: space-evenly !important;
}

.modal-bar-rect {
  width: 40px !important;
}

.modal-x-axis {
  height: 60px;
  margin-left: 65px;
}

.modal-x-label {
  font-size: 12px;
  text-align: center;
  margin-top: 12px;
}

.empty {
  text-align: center;
  color: #999;
  margin-top: 4rem;
}
</style>
